import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SearchBar } from "@/components/SearchBar";
import { JobCard, Job } from "@/components/JobCard";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { SlidersHorizontal, X } from "lucide-react";

const JOBS_PER_PAGE = 20;

const jobTypes = ["CLT", "PJ", "Estágio", "Freelance", "Temporário", "Consultor"];
const modalities = ["Presencial", "Híbrido", "Remoto"];
const categories = [
  "Administrativo",
  "Atendimento",
  "Comercial",
  "Design",
  "Jurídico",
  "Logística",
  "Marketing",
  "Outros",
  "Produção",
  "Recursos Humanos",
  "Saúde",
  "Tecnologia",
  "Vendas",
];

const VagasPage = () => {
  const [searchParams] = useSearchParams();
  const query = searchParams.get("q") || "";
  const estado = searchParams.get("estado") || "";
  const cidade = searchParams.get("cidade") || "";
  const categoria = searchParams.get("categoria") || "";
  const { toast } = useToast();

  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [selectedModalities, setSelectedModalities] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>(
    categoria ? [categoria] : []
  );
  const [showFilters, setShowFilters] = useState(false);
  const [savedJobIds, setSavedJobIds] = useState<Set<string>>(new Set());
  const [candidateProfileId, setCandidateProfileId] = useState<string | null>(null);

  const totalPages = Math.ceil(totalCount / JOBS_PER_PAGE);

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const from = (currentPage - 1) * JOBS_PER_PAGE;
      const to = from + JOBS_PER_PAGE - 1;

      let queryBuilder = supabase
        .from("jobs")
        .select(`
          *,
          company_profiles (
            company_name,
            logo_url
          )
        `, { count: 'exact' })
        .eq("is_active", true)
        .eq("status", "ativa");

      // Aplicar filtros de busca na query SQL
      if (query) {
        queryBuilder = queryBuilder.or(
          `title.ilike.%${query}%,description.ilike.%${query}%`
        );
      }

      if (estado) {
        queryBuilder = queryBuilder.eq("state", estado);
      }

      if (cidade) {
        queryBuilder = queryBuilder.ilike("city", `%${cidade}%`);
      }

      if (selectedCategories.length > 0) {
        queryBuilder = queryBuilder.in("area", selectedCategories);
      }

      // contract_type e job_type são arrays, precisam de overlaps
      if (selectedTypes.length > 0) {
        queryBuilder = queryBuilder.overlaps("contract_type", selectedTypes);
      }

      if (selectedModalities.length > 0) {
        queryBuilder = queryBuilder.overlaps("job_type", selectedModalities);
      }

      // Ordenação e paginação
      const { data, error, count } = await queryBuilder
        .order("created_at", { ascending: false })
        .range(from, to);

      if (error) throw error;

      setTotalCount(count || 0);

      const formattedJobs: Job[] = (data || []).map((job) => {
        // Format salary
        let salary: string | undefined;
        if (job.salary_range_start && job.salary_range_end) {
          salary = `R$ ${job.salary_range_start.toLocaleString("pt-BR")} - R$ ${job.salary_range_end.toLocaleString("pt-BR")}`;
        } else if (job.salary_range_start) {
          salary = `A partir de R$ ${job.salary_range_start.toLocaleString("pt-BR")}`;
        } else if (job.salary_range_end) {
          salary = `Até R$ ${job.salary_range_end.toLocaleString("pt-BR")}`;
        }

        // Format location
        const location = [job.city, job.state].filter(Boolean).join(", ") || "Não informado";

        // Get first contract type for display
        const type = job.contract_type?.[0] || "CLT";

        // Get first job type as modality
        const modality = job.job_type?.[0] || "Presencial";

        // Calculate if new (less than 7 days old)
        const createdDate = new Date(job.created_at);
        const now = new Date();
        const diffDays = Math.floor((now.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24));
        const isNew = diffDays <= 7;

        // Format posted date
        let postedAt: string;
        if (diffDays === 0) {
          postedAt = "Hoje";
        } else if (diffDays === 1) {
          postedAt = "Ontem";
        } else if (diffDays < 7) {
          postedAt = `${diffDays} dias atrás`;
        } else if (diffDays < 30) {
          const weeks = Math.floor(diffDays / 7);
          postedAt = `${weeks} semana${weeks > 1 ? "s" : ""} atrás`;
        } else {
          postedAt = createdDate.toLocaleDateString("pt-BR");
        }

        return {
          id: job.id,
          title: job.title,
          company: job.is_confidential ? "Confidencial" : (job.company_profiles?.company_name || "Empresa"),
          companyLogo: job.is_confidential ? undefined : (job.company_profiles?.logo_url || undefined),
          location,
          salary,
          type: type as Job["type"],
          modality: modality as Job["modality"],
          category: job.area || "Geral",
          postedAt,
          description: job.description,
          isNew,
          isFeatured: false,
        };
      });

      setJobs(formattedJobs);
    } catch (error) {
      console.error("Erro ao buscar vagas:", error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, query, estado, cidade, selectedTypes, selectedModalities, selectedCategories]);

  const fetchSavedJobs = useCallback(async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      const { data: profile } = await supabase
        .from("candidate_profiles")
        .select("id")
        .eq("user_id", user.id)
        .maybeSingle();

      if (profile) {
        setCandidateProfileId(profile.id);
        
        const { data: savedJobs } = await supabase
          .from("saved_jobs")
          .select("job_id")
          .eq("candidate_id", profile.id);

        if (savedJobs) {
          setSavedJobIds(new Set(savedJobs.map((s) => s.job_id)));
        }
      }
    }
  }, []);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  useEffect(() => {
    fetchSavedJobs();
  }, [fetchSavedJobs]);

  // Reset para página 1 quando filtros da URL mudam
  useEffect(() => {
    setCurrentPage(1);
  }, [query, estado, cidade, categoria]);

  const handleSaveToggle = async (jobId: string) => {
    if (!candidateProfileId) {
      toast({
        title: "Login necessário",
        description: "Faça login como candidato para salvar vagas.",
        variant: "destructive",
      });
      return;
    }

    const isSaved = savedJobIds.has(jobId);

    try {
      if (isSaved) {
        const { error } = await supabase
          .from("saved_jobs")
          .delete()
          .eq("job_id", jobId)
          .eq("candidate_id", candidateProfileId);

        if (error) throw error;

        setSavedJobIds((prev) => {
          const newSet = new Set(prev);
          newSet.delete(jobId);
          return newSet;
        });

        toast({
          title: "Vaga removida",
          description: "A vaga foi removida dos seus salvos.",
        });
      } else {
        const { error } = await supabase
          .from("saved_jobs")
          .insert({ job_id: jobId, candidate_id: candidateProfileId });

        if (error) throw error;

        setSavedJobIds((prev) => new Set(prev).add(jobId));

        toast({
          title: "Vaga salva!",
          description: "Você pode ver suas vagas salvas em 'Vagas Salvas'.",
        });
      }
    } catch (error) {
      console.error("Erro ao salvar/remover vaga:", error);
      toast({
        title: "Erro",
        description: "Não foi possível salvar/remover a vaga.",
        variant: "destructive",
      });
    }
  };

  const toggleFilter = (
    value: string,
    selected: string[],
    setSelected: React.Dispatch<React.SetStateAction<string[]>>
  ) => {
    setCurrentPage(1); // Reset para página 1 ao mudar filtro
    if (selected.includes(value)) {
      setSelected(selected.filter((v) => v !== value));
    } else {
      setSelected([...selected, value]);
    }
  };

  const clearAllFilters = () => {
    setCurrentPage(1);
    setSelectedTypes([]);
    setSelectedModalities([]);
    setSelectedCategories([]);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const generatePageNumbers = (): (number | "ellipsis")[] => {
    const pages: (number | "ellipsis")[] = [];
    
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      
      if (currentPage > 3) {
        pages.push("ellipsis");
      }
      
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        pages.push(i);
      }
      
      if (currentPage < totalPages - 2) {
        pages.push("ellipsis");
      }
      
      pages.push(totalPages);
    }
    
    return pages;
  };

  const hasActiveFilters = selectedTypes.length > 0 || selectedModalities.length > 0 || selectedCategories.length > 0;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />

      <main className="flex-1">
        {/* Search Header */}
        <section className="bg-card border-b py-8">
          <div className="container">
            <SearchBar />
          </div>
        </section>

        {/* Results Section */}
        <section className="py-8">
          <div className="container">
            <div className="mobile-container md:w-full flex flex-col lg:flex-row gap-8">
              {/* Filters - Desktop */}
              <aside className="hidden lg:block w-72 flex-shrink-0">
                <Card className="sticky top-24">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="font-display font-semibold">Filtros</h3>
                      {hasActiveFilters && (
                        <button
                          onClick={clearAllFilters}
                          className="text-sm text-primary hover:underline"
                        >
                          Limpar
                        </button>
                      )}
                    </div>

                    <div className="space-y-6">
                      {/* Type Filter */}
                      <div>
                        <h4 className="text-sm font-medium text-foreground mb-3">Tipo de Contratação</h4>
                        <div className="space-y-2">
                          {jobTypes.map((type) => (
                            <label key={type} className="flex items-center gap-2 cursor-pointer">
                              <Checkbox
                                checked={selectedTypes.includes(type)}
                                onCheckedChange={() => toggleFilter(type, selectedTypes, setSelectedTypes)}
                              />
                              <span className="text-sm text-muted-foreground">{type}</span>
                            </label>
                          ))}
                        </div>
                      </div>

                      {/* Modality Filter */}
                      <div>
                        <h4 className="text-sm font-medium text-foreground mb-3">Modalidade</h4>
                        <div className="space-y-2">
                          {modalities.map((modality) => (
                            <label key={modality} className="flex items-center gap-2 cursor-pointer">
                              <Checkbox
                                checked={selectedModalities.includes(modality)}
                                onCheckedChange={() => toggleFilter(modality, selectedModalities, setSelectedModalities)}
                              />
                              <span className="text-sm text-muted-foreground">{modality}</span>
                            </label>
                          ))}
                        </div>
                      </div>

                      {/* Category Filter */}
                      <div>
                        <h4 className="text-sm font-medium text-foreground mb-3">Categoria</h4>
                        <div className="space-y-2">
                          {categories.map((cat) => (
                            <label key={cat} className="flex items-center gap-2 cursor-pointer">
                              <Checkbox
                                checked={selectedCategories.includes(cat.toLowerCase())}
                                onCheckedChange={() => toggleFilter(cat.toLowerCase(), selectedCategories, setSelectedCategories)}
                              />
                              <span className="text-sm text-muted-foreground">{cat}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </aside>

              {/* Jobs List */}
              <div className="flex-1">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h1 className="text-2xl font-display font-bold text-foreground">
                      {query ? `Resultados para "${query}"` : "Todas as Vagas"}
                    </h1>
                    <p className="text-muted-foreground">
                      {loading 
                        ? "Carregando..." 
                        : totalCount === 0
                          ? "Nenhuma vaga encontrada"
                          : `Mostrando ${((currentPage - 1) * JOBS_PER_PAGE) + 1}-${Math.min(currentPage * JOBS_PER_PAGE, totalCount)} de ${totalCount} vaga${totalCount !== 1 ? "s" : ""}`
                      }
                    </p>
                  </div>

                  {/* Mobile Filter Button */}
                  <Button
                    variant="outline"
                    className="lg:hidden gap-2"
                    onClick={() => setShowFilters(!showFilters)}
                  >
                    <SlidersHorizontal className="h-4 w-4" />
                    Filtros
                    {hasActiveFilters && (
                      <Badge variant="default" className="ml-1">
                        {selectedTypes.length + selectedModalities.length + selectedCategories.length}
                      </Badge>
                    )}
                  </Button>
                </div>

                {/* Active Filters */}
                {hasActiveFilters && (
                  <div className="flex flex-wrap gap-2 mb-6">
                    {selectedTypes.map((type) => (
                      <Badge key={type} variant="secondary" className="gap-1 pr-1">
                        {type}
                        <button
                          onClick={() => toggleFilter(type, selectedTypes, setSelectedTypes)}
                          className="ml-1 hover:bg-muted rounded"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                    {selectedModalities.map((modality) => (
                      <Badge key={modality} variant="secondary" className="gap-1 pr-1">
                        {modality}
                        <button
                          onClick={() => toggleFilter(modality, selectedModalities, setSelectedModalities)}
                          className="ml-1 hover:bg-muted rounded"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                    {selectedCategories.map((cat) => (
                      <Badge key={cat} variant="secondary" className="gap-1 pr-1 capitalize">
                        {cat}
                        <button
                          onClick={() => toggleFilter(cat, selectedCategories, setSelectedCategories)}
                          className="ml-1 hover:bg-muted rounded"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}

                {/* Mobile Filters Panel */}
                {showFilters && (
                  <Card className="lg:hidden mb-6 animate-fade-in">
                    <CardContent className="p-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <h4 className="text-sm font-medium text-foreground mb-2">Tipo</h4>
                          <div className="space-y-1">
                            {jobTypes.map((type) => (
                              <label key={type} className="flex items-center gap-2 cursor-pointer">
                                <Checkbox
                                  checked={selectedTypes.includes(type)}
                                  onCheckedChange={() => toggleFilter(type, selectedTypes, setSelectedTypes)}
                                />
                                <span className="text-xs text-muted-foreground">{type}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                        <div>
                          <h4 className="text-sm font-medium text-foreground mb-2">Modalidade</h4>
                          <div className="space-y-1">
                            {modalities.map((modality) => (
                              <label key={modality} className="flex items-center gap-2 cursor-pointer">
                                <Checkbox
                                  checked={selectedModalities.includes(modality)}
                                  onCheckedChange={() => toggleFilter(modality, selectedModalities, setSelectedModalities)}
                                />
                                <span className="text-xs text-muted-foreground">{modality}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Jobs Grid */}
                {loading ? (
                  <div className="grid gap-4">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Card key={i}>
                        <CardContent className="p-6">
                          <div className="flex items-start gap-4 mb-4">
                            <Skeleton className="h-14 w-14 rounded-xl" />
                            <div className="flex-1">
                              <Skeleton className="h-5 w-48 mb-2" />
                              <Skeleton className="h-4 w-32" />
                            </div>
                          </div>
                          <Skeleton className="h-4 w-full mb-2" />
                          <Skeleton className="h-4 w-3/4 mb-4" />
                          <div className="flex gap-2">
                            <Skeleton className="h-6 w-16" />
                            <Skeleton className="h-6 w-20" />
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : jobs.length > 0 ? (
                  <>
                    <div className="grid gap-4">
                      {jobs.map((job) => (
                        <JobCard 
                          key={job.id} 
                          job={job} 
                          isSaved={savedJobIds.has(job.id)}
                          onSaveToggle={handleSaveToggle}
                        />
                      ))}
                    </div>

                    {/* Pagination */}
                    {totalCount > 0 && (
                      <Pagination className="mt-8">
                        <PaginationContent>
                          <PaginationItem>
                            <PaginationPrevious 
                              onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
                              className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                            />
                          </PaginationItem>
                          
                          {generatePageNumbers().map((pageNum, idx) => (
                            pageNum === "ellipsis" ? (
                              <PaginationItem key={`ellipsis-${idx}`}>
                                <PaginationEllipsis />
                              </PaginationItem>
                            ) : (
                              <PaginationItem key={pageNum}>
                                <PaginationLink
                                  onClick={() => handlePageChange(pageNum)}
                                  isActive={currentPage === pageNum}
                                  className="cursor-pointer"
                                >
                                  {pageNum}
                                </PaginationLink>
                              </PaginationItem>
                            )
                          ))}
                          
                          <PaginationItem>
                            <PaginationNext 
                              onClick={() => currentPage < totalPages && handlePageChange(currentPage + 1)}
                              className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                            />
                          </PaginationItem>
                        </PaginationContent>
                      </Pagination>
                    )}
                  </>
                ) : (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <p className="text-muted-foreground mb-4">
                        {totalCount === 0 && !hasActiveFilters
                          ? "Nenhuma vaga disponível no momento."
                          : "Nenhuma vaga encontrada com os filtros selecionados."
                        }
                      </p>
                      {hasActiveFilters && (
                        <Button variant="outline" onClick={clearAllFilters}>
                          Limpar Filtros
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default VagasPage;
